/* ==========================================================================
   eWear Bundle-Konfigurator
   Stellt Bundles aus den vorhandenen Produkten zusammen. Jedes Teil wird eine
   eigene Warenkorb-Position (Bestand und Größen bleiben korrekt) und trägt eine
   Bundle-Kennung. Der Preisvorteil kommt aus einem Rabattcode, der nach dem
   Hinzufügen angewendet wird.
   ========================================================================== */

(() => {
  class BundleBuilder {
    constructor(section) {
      this.section = section;
      this.products = JSON.parse(section.querySelector('[data-bundle-products]').textContent);
      this.bundles = JSON.parse(section.querySelector('[data-bundle-config]').textContent);
      this.strings = JSON.parse(section.querySelector('[data-bundle-strings]').textContent);
      this.dialog = section.querySelector('[data-bundle-dialog]');
      // Auf der eigenen Unterseite steht der Konfigurator fest auf der Seite statt im Dialog
      this.inline = this.dialog.hasAttribute('data-bundle-inline');
      this.slotsRoot = section.querySelector('[data-bundle-slots]');
      this.teamRoot = section.querySelector('[data-bundle-team]');
      this.addonRoot = section.querySelector('[data-bundle-addon]');
      this.status = section.querySelector('[data-bundle-status]');
      this.addButton = section.querySelector('.bundle-summary button');
      this.current = null;
      this.selection = [];
      this.addonChecked = false;

      section.querySelectorAll('[data-bundle-open]').forEach((button) => {
        button.addEventListener('click', () => this.open(Number(button.dataset.bundleOpen)));
      });
      this.sizeguide = section.querySelector('[data-bundle-sizeguide]');
      const sizeguideClose = section.querySelector('[data-bundle-sizeguide-close]');
      if (sizeguideClose) sizeguideClose.addEventListener('click', () => this.sizeguide.close());
      if (this.sizeguide) {
        // Klick auf den dunklen Rand schließt das Fenster
        this.sizeguide.addEventListener('click', (event) => {
          if (event.target === this.sizeguide) this.sizeguide.close();
        });
      }

      const closeButton = section.querySelector('[data-bundle-close]');
      if (closeButton) closeButton.addEventListener('click', () => this.dialog.close());
      this.addButton.addEventListener('click', () => this.addToCart());
      this.renderCardPrices();
      this.setupGalleries();

      // Unterseite: das eine Bundle ist sofort offen, ohne Klick
      if (this.inline) this.open(0);
    }

    /**
     * Bildergalerien: Punkte und Pfeile schalten die Ansicht um.
     * Auf den Kacheln liegt der Link als eigene Fläche darüber – die Knöpfe sind
     * Geschwister davon, ein Klick darauf löst den Link also nicht aus.
     */
    setupGalleries() {
      this.section.querySelectorAll('[data-bundle-gallery]').forEach((galerie) => {
        const bilder = [...galerie.querySelectorAll('.bundle-gallery__img')];
        const punkte = [...galerie.querySelectorAll('.bundle-gallery__dot')];
        if (bilder.length < 2) return;

        let aktuell = 0;
        const zeige = (index) => {
          aktuell = (index + bilder.length) % bilder.length;
          bilder.forEach((b, i) => b.classList.toggle('is-current', i === aktuell));
          punkte.forEach((p, i) => {
            p.classList.toggle('is-current', i === aktuell);
            p.setAttribute('aria-current', i === aktuell ? 'true' : 'false');
          });
        };

        galerie.addEventListener('click', (event) => {
          const ziel = event.target.closest('[data-go], [data-step]');
          if (!ziel || !galerie.contains(ziel)) return;
          event.preventDefault();
          event.stopPropagation();
          if (ziel.dataset.go !== undefined) zeige(Number(ziel.dataset.go));
          else zeige(aktuell + Number(ziel.dataset.step));
        });

        // Wischen auf dem Handy
        let startX = null;
        galerie.addEventListener('touchstart', (e) => { startX = e.changedTouches[0].clientX; }, { passive: true });
        galerie.addEventListener('touchend', (e) => {
          if (startX === null) return;
          const weg = e.changedTouches[0].clientX - startX;
          if (Math.abs(weg) > 40) zeige(aktuell + (weg < 0 ? 1 : -1));
          startX = null;
        }, { passive: true });
      });
    }

    /** Günstigstmögliche Zusammenstellung je Bundle für die Karte ("ab …"). */
    renderCardPrices() {
      this.bundles.forEach((bundle, index) => {
        const target = this.section.querySelector(`[data-bundle-from="${index}"]`);
        const badge = this.section.querySelector(`[data-bundle-saving="${index}"]`);
        if (!target && !badge) return;

        // Günstigste tatsächlich lieferbare Variante je Position.
        // Liefert aktuellen Preis und Vergleichspreis derselben Variante.
        const cheapest = (handles) => {
          const kandidaten = handles
            .flatMap((handle) => this.variantsOf(handle)
              .filter((variant) => variant.available && (variant.policy === 'continue' || variant.quantity > 0)));
          if (!kandidaten.length) return null;
          const guenstigste = kandidaten.reduce((a, b) => (b.price < a.price ? b : a));
          return { price: guenstigste.price, compare: guenstigste.compareAtPrice || guenstigste.price };
        };

        const slots = this.slotsFor(bundle);
        let values;

        if (bundle.mode === 'same') {
          // Alle Teile sind dasselbe Produkt: das günstigste Produkt bestimmt den Preis
          const proProdukt = slots[0].handles.map((handle) => cheapest([handle])).filter(Boolean);
          if (!proProdukt.length) return;
          const guenstigstes = proProdukt.reduce((a, b) => (b.price < a.price ? b : a));
          values = slots.map(() => guenstigstes);
        } else {
          values = slots.map((slot) => cheapest(slot.handles));
        }

        if (!values.length || values.some((value) => !value)) return;

        const aktuell = values.reduce((summe, v) => summe + v.price, 0);
        // Vergleichspreis auf volle Euro abgerundet, damit die Ersparnis nie größer wirkt als sie ist
        const vergleich = Math.floor(values.reduce((summe, v) => summe + v.compare, 0) / 100) * 100;
        // "ab"-Summe auf den nächsten vollen Euro aufgerundet: der echte Preis liegt nie darüber
        const total = Math.ceil(aktuell * (1 - bundle.percent / 100) / 100) * 100;

        // Durchgestrichen steht der alte Vergleichspreis, damit die volle Ersparnis sichtbar wird
        if (target) target.innerHTML = `${this.strings.from.replace('__p__', this.moneyFull(total))} <s>${this.moneyFull(vergleich)}</s>`;

        // Das Badge zeigt nicht die guenstigste, sondern die vorteilhafteste Zusammenstellung.
        // Die Hose ist staerker gefallen als die uebrigen Teile, ein Bundle aus Hosen spart
        // also prozentual mehr, obwohl es nicht das billigste ist.
        if (badge) {
          const best = this.bestSaving(bundle, slots, cheapest);
          // Winziger Zuschlag gegen Gleitkomma-Reste: 148 x 0,9 ergibt 133,20000000000002,
          // daraus wurden 9,999... Prozent und abgerundet 9 statt 10.
          if (best !== null) badge.textContent = `${Math.floor(best + 1e-6)} %`;
        }
      });
    }

    /**
     * Hoechstmoegliche prozentuale Ersparnis eines Bundles gegenueber den Vergleichspreisen.
     * Prueft alle erlaubten Produktkombinationen, nicht nur die guenstigste – gerundet wird
     * abwaerts, damit der ausgewiesene Wert nie ueber dem tatsaechlich erreichbaren liegt.
     */
    bestSaving(bundle, slots, cheapest) {
      const anteil = 1 - bundle.percent / 100;
      const quote = (werte) => {
        const aktuell = werte.reduce((s, v) => s + v.price, 0);
        const vergleich = werte.reduce((s, v) => s + v.compare, 0);
        if (!vergleich) return null;
        return (1 - (aktuell * anteil) / vergleich) * 100;
      };

      if (bundle.mode === 'same') {
        // Alle Teile sind dasselbe Produkt: jedes erlaubte Produkt einmal durchrechnen
        const werte = slots[0].handles.map((handle) => cheapest([handle])).filter(Boolean);
        const treffer = werte
          .map((v) => quote(new Array(slots.length).fill(v)))
          .filter((x) => x !== null);
        return treffer.length ? Math.max(...treffer) : null;
      }

      // Feste Positionen: alle Kombinationen durchspielen. In der Praxis sind das
      // eine Handvoll, der Deckel schuetzt nur vor einer entgleisten Konfiguration.
      const optionen = slots.map((slot) => slot.handles.map((h) => cheapest([h])).filter(Boolean));
      if (optionen.some((o) => !o.length)) return null;
      if (optionen.reduce((n, o) => n * o.length, 1) > 500) {
        // Notnagel: je Position die Variante mit dem besten Verhaeltnis
        const beste = optionen.map((o) => o.reduce((a, b) => (b.price / b.compare < a.price / a.compare ? b : a)));
        return quote(beste);
      }

      const kombinationen = optionen.reduce(
        (bisher, o) => bisher.flatMap((teil) => o.map((v) => [...teil, v])),
        [[]]
      );
      const treffer = kombinationen.map(quote).filter((x) => x !== null);
      return treffer.length ? Math.max(...treffer) : null;
    }

    money(cents) {
      return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' });
    }

    /**
     * Wie money(), nur ohne Kommastellen – für die Teaser-Preise auf den Bundle-Karten.
     * Die Summe muss vorher schon auf volle Euro gerundet sein.
     */
    moneyFull(cents) {
      return (cents / 100).toLocaleString('de-DE', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }

    /**
     * Lagerbestand einer Variante, abzüglich der Stücke, die weiter oben im Bundle
     * bereits dieselbe Variante belegen. So wird nur die überzählige Position gemeldet.
     */
    stockLeft(handle, variantId, slotIndex) {
      const variant = this.products[handle]?.variants.find((v) => v.id === variantId);
      if (!variant) return 0;
      if (variant.policy === 'continue') return 99;
      const usedBefore = this.selection.filter((slot, index) => index < slotIndex && slot.handle === handle && slot.variantId === variantId).length;
      return variant.quantity - usedBefore;
    }

    variantsOf(handle) {
      return this.products[handle]?.variants || [];
    }

    valuesFor(handle, position, filter = []) {
      const values = [];
      this.variantsOf(handle).forEach((variant) => {
        if (!variant.available) return;
        if (filter.some((value, index) => value && variant.options[index] !== value)) return;
        if (!values.includes(variant.options[position])) values.push(variant.options[position]);
      });
      return values;
    }

    /**
     * Größenauswahl als Knopfreihe. Jede Größe des Produkts ist sichtbar, auch wenn sie
     * nicht lieferbar ist – dann ausgegraut und gesperrt. Bei lieferbaren steht der
     * Restbestand dabei, ab drei oder weniger rot.
     */
    buildSizes(slotIndex, chosen, product, optionName, position) {
      const reihe = document.createElement('div');
      reihe.className = 'bundle-sizes';
      reihe.setAttribute('role', 'group');
      reihe.setAttribute('aria-label', optionName);

      this.allValuesFor(chosen.handle, position).forEach((wert) => {
        const kombination = [...chosen.options];
        kombination[position] = wert;
        const variant = this.findVariant(chosen.handle, kombination);

        const unbegrenzt = Boolean(variant && variant.policy === 'continue');
        const rest = variant && variant.available && !unbegrenzt
          ? this.stockLeft(chosen.handle, variant.id, slotIndex)
          : 0;
        const waehlbar = Boolean(variant && variant.available && (unbegrenzt || rest > 0));
        const knapp = waehlbar && !unbegrenzt && rest <= 3;

        const knopf = document.createElement('button');
        knopf.type = 'button';
        knopf.className = 'bundle-size';
        if (!waehlbar) knopf.classList.add('is-out');
        if (knapp) knopf.classList.add('is-low');
        if (wert === chosen.options[position]) knopf.classList.add('is-current');
        knopf.disabled = !waehlbar;
        knopf.setAttribute('aria-pressed', wert === chosen.options[position] ? 'true' : 'false');

        const label = document.createElement('span');
        label.className = 'bundle-size__label';
        label.textContent = wert;
        knopf.appendChild(label);

        const zusatz = document.createElement('span');
        zusatz.className = 'bundle-size__count';
        if (!waehlbar) {
          zusatz.textContent = this.strings.soldOutShort || '–';
        } else if (!unbegrenzt) {
          zusatz.textContent = (this.strings.leftCount || '__n__').replace('__n__', rest);
        }
        if (zusatz.textContent) knopf.appendChild(zusatz);

        if (waehlbar) {
          knopf.addEventListener('click', () => this.setOption(slotIndex, position, wert));
        } else {
          knopf.title = this.strings.soldOut || '';
        }
        reihe.appendChild(knopf);
      });

      return reihe;
    }

    /** Alle Werte einer Option – im Gegensatz zu valuesFor() auch die ausverkauften. */
    allValuesFor(handle, position) {
      const werte = [];
      this.variantsOf(handle).forEach((variant) => {
        const w = variant.options[position];
        if (w !== undefined && !werte.includes(w)) werte.push(w);
      });
      return werte;
    }

    findVariant(handle, options) {
      return this.variantsOf(handle).find((variant) => variant.options.every((value, index) => value === options[index]));
    }

    /** Positionen eines Bundles: feste Slots oder N-mal dasselbe Produkt. */
    slotsFor(bundle) {
      if (bundle.mode === 'same') {
        const handles = (bundle.products || '').split(',').map((h) => h.trim()).filter(Boolean);
        return Array.from({ length: bundle.quantity }, (_, index) => ({
          label: this.strings.part.replace('__n__', index + 1),
          handles,
          locked: index > 0 // Produkt wird einmal gewählt und gilt für alle Teile
        }));
      }
      return (bundle.slots || '')
        .split(/\r?\n/)
        .map((line) => {
          const [label, handleList] = line.includes('|') ? line.split('|') : ['Teil', line];
          return { label: label.trim(), handles: (handleList || '').split(',').map((h) => h.trim()).filter(Boolean) };
        })
        .filter((slot) => slot.handles.length);
    }

    open(index) {
      const bundle = this.bundles[index];
      if (!bundle) return;
      this.current = bundle;
      this.slotDefs = this.slotsFor(bundle);
      this.addonChecked = false;

      // Startauswahl: erstes Produkt, dann je Position die erste Variante, die noch auf Lager ist.
      // So startet auch ein Squad-Bundle mit fünf tatsächlich lieferbaren Teilen.
      this.selection = [];
      this.slotDefs.forEach((slot, index) => {
        const handle = slot.handles[0];
        const candidates = this.variantsOf(handle).filter((variant) => variant.available);
        const used = (variantId) => this.selection.filter((entry) => entry.handle === handle && entry.variantId === variantId).length;
        const pick = candidates.find((variant) => variant.policy === 'continue' || variant.quantity > used(variant.id)) || candidates[0];
        this.selection[index] = { handle, variantId: pick?.id || null, options: pick ? [...pick.options] : [] };
      });

      this.section.querySelector('[data-bundle-title]').textContent = bundle.title;
      this.section.querySelector('[data-bundle-subtitle]').textContent = bundle.text || '';
      this.section.querySelector('[data-bundle-discount-label]').textContent = bundle.percent > 0 ? `${this.strings.discount} ${bundle.percent} %` : this.strings.discount;
      this.status.textContent = '';
      this.renderSlots();
      this.renderAddon();
      this.renderTeam();
      this.update();
      if (!this.inline) this.dialog.showModal();
    }

    renderSlots() {
      this.slotsRoot.innerHTML = '';

      this.slotDefs.forEach((def, index) => {
        const chosen = this.selection[index];
        const product = this.products[chosen.handle];
        const row = document.createElement('div');
        row.className = 'bundle-slot';

        const thumb = document.createElement('div');
        thumb.className = 'bundle-slot__thumb';
        // Bild der gewählten Variante, damit der Wechsel der Farbe sofort sichtbar ist
        const gewaehlt = this.variantsOf(chosen.handle).find((v) => v.id === chosen.variantId);
        const bild = gewaehlt?.image || product?.image;
        if (bild) {
          const img = document.createElement('img');
          img.src = bild;
          img.alt = '';
          img.loading = 'lazy';
          thumb.appendChild(img);
        }

        const body = document.createElement('div');
        const label = document.createElement('p');
        label.className = 'bundle-slot__label';
        label.textContent = def.label;
        const selects = document.createElement('div');
        selects.className = 'bundle-slot__selects';

        // Produktauswahl, wenn die Position mehrere Produkte erlaubt
        if (def.handles.length > 1 && !def.locked) {
          const productSelect = document.createElement('select');
          productSelect.setAttribute('aria-label', `${def.label}: ${this.strings.product}`);
          def.handles.forEach((handle) => {
            if (!this.products[handle]) return;
            const option = document.createElement('option');
            option.value = handle;
            option.textContent = this.products[handle].title;
            option.selected = handle === chosen.handle;
            productSelect.appendChild(option);
          });
          productSelect.addEventListener('change', () => this.setProduct(index, productSelect.value));
          selects.appendChild(productSelect);
        }

        // Farbe und Größe (bzw. alle Optionen des Produkts)
        (product?.options || []).forEach((optionName, position) => {
          const letzte = position === (product.options.length - 1);
          const istGroesse = /gr(ö|oe)ße|size/i.test(optionName) || letzte;

          if (istGroesse) {
            // Größen als Knöpfe: alle sichtbar, ausverkaufte ausgegraut und gesperrt,
            // verfügbare mit Restbestand – ab drei oder weniger in Rot.
            selects.appendChild(this.buildSizes(index, chosen, product, optionName, position));
            return;
          }

          const select = document.createElement('select');
          select.setAttribute('aria-label', `${def.label}: ${optionName}`);
          const filter = chosen.options.map((value, i) => (i < position ? value : null));
          this.valuesFor(chosen.handle, position, filter).forEach((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = `${optionName}: ${value}`;
            option.selected = value === chosen.options[position];
            select.appendChild(option);
          });
          select.addEventListener('change', () => this.setOption(index, position, select.value));
          selects.appendChild(select);
        });

        // Größentabelle des gewählten Produkts, falls hinterlegt
        if (product?.sizeGuide && this.sizeguide) {
          const knopf = document.createElement('button');
          knopf.type = 'button';
          knopf.className = 'bundle-slot__sizeguide';
          knopf.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 9h20v6H2z"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>';
          knopf.append(this.strings.sizeChart || 'Size chart');
          knopf.addEventListener('click', () => {
            this.section.querySelector('[data-bundle-sizeguide-image]').src = product.sizeGuide;
            this.sizeguide.showModal();
          });
          selects.appendChild(knopf);
        }

        const stock = document.createElement('p');
        stock.className = 'bundle-slot__stock';
        stock.dataset.slotStock = index;

        body.append(label, selects, stock);
        row.append(thumb, body);
        this.slotsRoot.appendChild(row);
      });
    }

    renderAddon() {
      const addon = this.products[this.current.addon];
      this.addonRoot.hidden = !addon;
      if (!addon) return;
      const variant = addon.variants.find((v) => v.available);
      this.addonRoot.innerHTML = '';
      if (!variant) { this.addonRoot.hidden = true; return; }

      const id = `bundle-addon-${Math.random().toString(36).slice(2)}`;
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.id = id;
      input.checked = this.addonChecked;
      input.addEventListener('change', () => { this.addonChecked = input.checked; this.update(); });

      const label = document.createElement('label');
      label.htmlFor = id;
      // Name als Text setzen, damit Produktnamen aus Shopify nicht als Markup landen
      label.textContent = `${this.current.addon_label || addon.title} `;

      // Hat der Zusatzartikel einen Vergleichspreis, steht er durchgestrichen daneben
      const zusatz = document.createElement('span');
      if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        zusatz.innerHTML = this.strings.addonSuffixCompare
          .replace('__p__', this.money(variant.price))
          .replace('__c__', `<s>${this.money(variant.compareAtPrice)}</s>`);
      } else {
        zusatz.textContent = this.strings.addonSuffix.replace('__p__', this.money(variant.price));
      }
      label.appendChild(zusatz);

      this.addonRoot.append(input, label);
    }

    setProduct(index, handle) {
      const first = this.variantsOf(handle).find((variant) => variant.available);
      // Bei "mehrfach dasselbe Produkt" gilt die Wahl für alle Positionen; dabei erneut auf Lager verteilen
      const targets = this.current.mode === 'same' ? this.slotDefs.map((_, i) => i) : [index];
      const candidates = this.variantsOf(handle).filter((variant) => variant.available);
      targets.forEach((slotIndex) => {
        const used = (variantId) => this.selection.filter((entry, i) => targets.includes(i) && i < slotIndex && entry.handle === handle && entry.variantId === variantId).length;
        const pick = candidates.find((variant) => variant.policy === 'continue' || variant.quantity > used(variant.id)) || first;
        this.selection[slotIndex] = { handle, variantId: pick?.id || null, options: pick ? [...pick.options] : [] };
      });
      this.renderSlots();
      this.update();
    }

    setOption(index, position, value) {
      const slot = this.selection[index];
      const options = [...slot.options];
      options[position] = value;

      // Nachfolgende Optionen prüfen und notfalls auf den ersten lieferbaren Wert setzen
      for (let next = position + 1; next < options.length; next++) {
        const allowed = this.valuesFor(slot.handle, next, options.map((v, i) => (i < next ? v : null)));
        if (!allowed.includes(options[next])) options[next] = allowed[0];
      }

      const variant = this.findVariant(slot.handle, options);
      this.selection[index] = { ...slot, options, variantId: variant?.id || null };
      this.renderSlots();
      this.update();
    }

    update() {
      let regular = 0;
      let discountable = 0;
      let vergleichSumme = 0;
      let problem = null;

      this.selection.forEach((slot, index) => {
        const variant = this.variantsOf(slot.handle).find((v) => v.id === slot.variantId);
        const note = this.section.querySelector(`[data-slot-stock="${index}"]`);
        if (!variant) {
          if (note) { note.textContent = this.strings.noVariant; note.dataset.state = 'warn'; }
          problem = this.strings.pickAll;
          return;
        }

        regular += variant.price;
        discountable += variant.price;
        vergleichSumme += variant.compareAtPrice || variant.price;

        const left = this.stockLeft(slot.handle, slot.variantId, index);
        if (note) {
          if (left <= 0) {
            note.textContent = this.strings.soldOut;
            note.dataset.state = 'warn';
            problem = this.strings.oneUnavailable;
          } else {
            // Der Restbestand steht jetzt an den Größen-Knöpfen, hier nur noch Probleme
            note.textContent = '';
            note.dataset.state = '';
          }
        }
      });

      const addon = this.products[this.current.addon];
      const addonVariant = this.addonChecked ? addon?.variants.find((v) => v.available) : null;
      if (addonVariant) {
        regular += addonVariant.price;
        vergleichSumme += addonVariant.compareAtPrice || addonVariant.price;
      }

      const save = Math.round((discountable * this.current.percent) / 100);
      this.section.querySelector('[data-bundle-regular]').textContent = this.money(regular);
      this.section.querySelector('[data-bundle-save]').textContent = `− ${this.money(save)}`;
      this.section.querySelector('[data-bundle-total]').textContent = this.money(regular - save);

      // Gesamtersparnis gegenüber den alten Preisen – passt zur Prozentzahl auf der Kachel
      const gesamt = regular - save;
      const sparZeilen = this.section.querySelectorAll('[data-bundle-compare-row]');
      if (sparZeilen.length) {
        const zeigen = vergleichSumme > gesamt;
        sparZeilen.forEach((z) => { z.hidden = !zeigen; });
        if (zeigen) {
          // Im Konfigurator steht der echte Wert: Prozent auf ganze Zahlen gerundet,
          // der Betrag auf den Cent genau. Die 5er-Stufen mit ">" gelten nur fuer die
          // Kachel-Badges, wo eine Spanne ueber alle Zusammenstellungen gezeigt wird.
          const prozent = Math.round((1 - gesamt / vergleichSumme) * 100);
          this.section.querySelectorAll('[data-bundle-compare]').forEach((e) => { e.textContent = this.money(vergleichSumme); });
          this.section.querySelectorAll('[data-bundle-saving-total]').forEach((e) => {
            e.textContent = `− ${this.money(vergleichSumme - gesamt)} (${prozent} %)`;
          });
        }
      }

      if (!problem && this.teamBlockiert && this.teamBlockiert()) {
        problem = this.strings.teamRightsMissing;
      }

      this.addButton.disabled = Boolean(problem);
      if (problem) {
        this.status.dataset.state = 'error';
        this.status.textContent = problem;
      } else if (this.status.dataset.state === 'error') {
        this.status.dataset.state = '';
        this.status.textContent = '';
      }
    }

    /**
     * Team-Personalisierung: ein Namensfeld je Teil und ein Logo fuers ganze Team.
     * Gehoert zum Squad-Bundle und kostet nichts. Alles freiwillig – wer nichts
     * eintraegt, bestellt wie bisher.
     */
    renderTeam() {
      if (!this.teamRoot) return;
      this.teamNamen = [];
      this.teamLogo = null;
      this.teamRoot.innerHTML = '';

      if (!this.current.team) {
        this.teamRoot.hidden = true;
        return;
      }
      this.teamRoot.hidden = false;

      const t = this.strings;
      const kopf = document.createElement('div');
      kopf.className = 'bundle-team__kopf';
      const titel = document.createElement('p');
      titel.className = 'bundle-team__titel';
      titel.textContent = t.teamTitle;
      const text = document.createElement('p');
      text.className = 'bundle-team__text';
      text.textContent = t.teamText;
      kopf.append(titel, text);

      const namen = document.createElement('div');
      namen.className = 'bundle-team__namen';
      this.slotDefs.forEach((def, i) => {
        const feld = document.createElement('label');
        feld.className = 'bundle-team__feld';
        const label = document.createElement('span');
        label.className = 'bundle-team__label';
        label.textContent = t.teamPlayer.replace('__n__', i + 1);
        const eingabe = document.createElement('input');
        eingabe.type = 'text';
        eingabe.maxLength = 14;
        eingabe.autocomplete = 'off';
        eingabe.placeholder = t.teamPlaceholder;
        eingabe.addEventListener('input', () => {
          this.teamNamen[i] = eingabe.value.trim();
        });
        feld.append(label, eingabe);
        namen.appendChild(feld);
      });

      // ---- Logo ----
      const logo = document.createElement('div');
      logo.className = 'bundle-team__logo';

      const waehlen = document.createElement('label');
      waehlen.className = 'bundle-team__waehlen';
      const datei = document.createElement('input');
      datei.type = 'file';
      datei.accept = 'image/png,image/svg+xml';
      const waehlenText = document.createElement('span');
      waehlenText.textContent = t.teamChooseLogo;
      waehlen.append(datei, waehlenText);

      const vorschau = document.createElement('div');
      vorschau.className = 'bundle-team__vorschau';
      vorschau.hidden = true;
      const vorschauBild = document.createElement('img');
      vorschauBild.alt = '';
      vorschau.appendChild(vorschauBild);

      const name = document.createElement('span');
      name.className = 'bundle-team__dateiname';

      const weg = document.createElement('button');
      weg.type = 'button';
      weg.className = 'bundle-team__weg';
      weg.textContent = t.teamRemove;
      weg.hidden = true;

      const status = document.createElement('p');
      status.className = 'bundle-team__status';

      const rechte = document.createElement('label');
      rechte.className = 'bundle-team__rechte';
      rechte.hidden = true;
      const haken = document.createElement('input');
      haken.type = 'checkbox';
      const rechteText = document.createElement('span');
      rechteText.textContent = t.teamRights;
      rechte.append(haken, rechteText);

      const zuruecksetzen = () => {
        this.teamLogo = null;
        datei.value = '';
        vorschau.hidden = true;
        weg.hidden = true;
        rechte.hidden = true;
        haken.checked = false;
        name.textContent = '';
        status.dataset.state = '';
        status.textContent = '';
        this.update();
      };

      datei.addEventListener('change', () => {
        const f = datei.files && datei.files[0];
        if (!f) return zuruecksetzen();
        const erlaubt = ['image/png', 'image/svg+xml'];
        if (!erlaubt.includes(f.type)) {
          status.dataset.state = 'fehler';
          status.textContent = t.teamWrongType;
          datei.value = '';
          return;
        }
        if (f.size > 8 * 1024 * 1024) {
          status.dataset.state = 'fehler';
          status.textContent = t.teamTooBig;
          datei.value = '';
          return;
        }
        this.teamLogo = f;
        vorschauBild.src = URL.createObjectURL(f);
        vorschau.hidden = false;
        weg.hidden = false;
        rechte.hidden = false;
        name.textContent = f.name;
        status.dataset.state = '';
        status.textContent = '';
        this.update();
      });

      weg.addEventListener('click', zuruecksetzen);
      haken.addEventListener('change', () => this.update());

      logo.append(waehlen, vorschau, name, weg);
      this.teamRoot.append(kopf, namen, logo, rechte, status);
      this.teamRechteHaken = haken;
      this.teamStatus = status;
    }

    /** Fehlt die Rechtebestaetigung zum hochgeladenen Logo? */
    teamBlockiert() {
      return Boolean(this.teamLogo) && this.teamRechteHaken && !this.teamRechteHaken.checked;
    }

    async addToCart() {
      const bundleId = `${this.current.title.replace(/\s+/g, '-').toLowerCase()}-${Date.now().toString(36)}`;
      const items = this.selection
        .map((slot, i) => ({ slot, i }))
        .filter(({ slot }) => slot.variantId)
        .map(({ slot, i }) => {
          const properties = { Bundle: this.current.title, _bundle_id: bundleId };
          const name = (this.teamNamen || [])[i];
          if (name) properties[this.strings.teamNameProperty] = name;
          return { id: slot.variantId, quantity: 1, properties };
        });

      const addon = this.products[this.current.addon];
      const addonVariant = this.addonChecked ? addon?.variants.find((v) => v.available) : null;
      if (addonVariant) items.push({ id: addonVariant.id, quantity: 1, properties: { Bundle: `${this.current.title} (Zusatz)`, _bundle_id: bundleId } });

      this.addButton.disabled = true;
      this.status.dataset.state = '';
      this.status.textContent = this.strings.adding;

      try {
        const root = window.Shopify?.routes?.root || '/';

        // Eine Datei laesst sich nicht als JSON senden. Deshalb geht die erste
        // Position samt Logo als Formulardaten raus; Shopify legt die Datei
        // dauerhaft ab und gibt ihre Adresse zurueck. Die uebrigen Positionen
        // bekommen diese Adresse als Text mit. Schlaegt der zweite Schritt fehl,
        // nehmen wir die erste Position wieder aus dem Warenkorb.
        let erste = null;
        if (this.teamLogo && items.length) {
          const formular = new FormData();
          formular.append('id', items[0].id);
          formular.append('quantity', '1');
          Object.entries(items[0].properties).forEach(([k, v]) => formular.append(`properties[${k}]`, v));
          formular.append(`properties[${this.strings.teamLogoProperty}]`, this.teamLogo);

          const antwort = await fetch(`${root}cart/add.js`, { method: 'POST', body: formular });
          if (!antwort.ok) throw new Error(await antwort.text());
          const zeile = await antwort.json();
          erste = zeile.key || null;
          const adresse = (zeile.properties || {})[this.strings.teamLogoProperty];
          items.shift();
          if (adresse) {
            items.forEach((item) => {
              item.properties[this.strings.teamLogoProperty] = adresse;
            });
          }
        }

        if (items.length) {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!response.ok) {
            if (erste) {
              await fetch(`${root}cart/change.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: erste, quantity: 0 })
              }).catch(() => {});
            }
            throw new Error(await response.text());
          }
        }

        // Rabattcode anwenden und in den Warenkorb wechseln
        const code = (this.current.code || '').trim();
        window.location.href = code ? `${root}discount/${encodeURIComponent(code)}?redirect=${root}cart` : `${root}cart`;
      } catch (error) {
        this.status.dataset.state = 'error';
        this.status.textContent = this.strings.failed;
        this.addButton.disabled = false;
      }
    }
  }

  const init = () => {
    document.querySelectorAll('[data-bundles]').forEach((node) => {
      const section = node.closest('.shopify-section') || document;
      if (section.dataset?.bundlesReady) return;
      if (section.dataset) section.dataset.bundlesReady = 'true';
      new BundleBuilder(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
